import {useTrackSearch} from '@aboutyou/tracking';
import {useEffect, useRef, useState} from 'react';
import {useIsHintCovered} from '../../../../features/ShopHint/hooks/useIsHintCovered';
import {useRectEffect} from 'react-viewport-utils';
import {useOnClickOutside} from '@aboutyou/ui-components';
import {useSearchBarPromotionData} from '../../../../shared-deprecated/useSearchBarPromotionData';
import {SearchNameType} from '@aboutyou/legacy-tracking-sdk';
import {useStoredSearchTerms} from './useStoredSearchTerms';

export type onClickSuggestionType = ({
  searchTerm,
  searchType,
  url,
}: {
  searchTerm: string;
  searchType: SearchNameType;
  url: string;
}) => void;

export const useSearchBar = ({
  isSearchBarExpanded,
  collapseSearchBar,
  expandSearchBar,
  limitSearchHistory,
}: {
  isSearchBarExpanded: boolean;
  collapseSearchBar?: VoidFunction;
  expandSearchBar?: VoidFunction;
  limitSearchHistory?: boolean;
}) => {
  const trackSearch = useTrackSearch();
  const {recentTerms, addSearchTerm} = useStoredSearchTerms({
    limitSearchHistory,
  });

  const [searchValue, setSearchValue] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isTabletMode, setIsTabletMode] = useState(false);
  const {setIsHintCovered} = useIsHintCovered();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchBarRef = useRef<HTMLDivElement | null>(null);
  const suggestionSelected = useRef(false);

  useRectEffect(
    rect => {
      if (rect) {
        setIsTabletMode(rect?.width < 180);
      }
    },
    wrapperRef,
    {priority: 'low'},
  );

  useEffect(() => {
    if (isSearchBarExpanded) {
      inputRef?.current?.focus();
    }
  }, [isSearchBarExpanded]);

  const onFocusSearchInput = () => {
    if (!isInputFocused) {
      trackSearch({
        searchAction: 'click',
        searchName: 'searchOpen',
      });
      setIsInputFocused(true);
      setIsHintCovered(true);
    }
  };

  const collapseAndBlurInput = (shouldCollapse = true) => {
    if (shouldCollapse) {
      collapseSearchBar?.();
    }
    suggestionSelected.current = false;
    setIsInputFocused(false);
    setIsHintCovered(false);
    inputRef?.current?.blur();
  };

  const closeSearchResult = () => {
    if (isInputFocused) {
      // This prevents accidental posting the searchClose-event
      // when the user has clicked on a search result. Input will then be
      // blurred and we have already posted an search event e.g. "suggestedCategory"
      trackSearch({
        searchAction: 'click',
        searchName: 'searchClose',
      });
    }
    collapseAndBlurInput();
  };

  const handleRemoveInputFocus = () => {
    if (isInputFocused) {
      collapseAndBlurInput(false);
    }
  };

  // This will be called after the user pressed enter
  const onRemoveInputFocusAndAddSearchTermToLocalStorage = (
    searchTerm: string,
  ) => {
    addSearchTerm({term: searchTerm});
    handleRemoveInputFocus();
  };

  const handleInputOnChange = (term: string) => {
    setSearchValue(term);
  };

  const handleCloseIconTap = () => {
    trackSearch({
      searchAction: 'click',
      searchName: 'searchClose',
    });
    collapseAndBlurInput();
  };

  const handleOnClickSearchIcon = () => {
    if (isTabletMode) {
      expandSearchBar?.();
    }
  };

  const setSuggestionSelected = (suggestionsListIndex: number | null) => {
    suggestionSelected.current = Boolean(suggestionsListIndex);
  };

  useOnClickOutside(wrapperRef, closeSearchResult, {
    trigger: 'onceAndReset',
  });

  const {promotionData} = useSearchBarPromotionData({
    isRequestDisabled: !isInputFocused,
  });

  const shouldShowCloseIcon = isTabletMode && isSearchBarExpanded;

  const shouldShowSearchResults =
    isInputFocused &&
    (promotionData ||
      searchValue.length > 0 ||
      (searchValue.length === 0 && recentTerms.length > 0));

  const shouldShowBasSearchTeaser = searchValue.length === 0;

  return {
    searchBarRef,
    wrapperRef,
    inputRef,
    isTabletMode,
    isInputFocused,
    shouldShowSearchResults,
    shouldShowBasSearchTeaser,
    promotionData,
    searchValue,
    onFocusSearchInput,
    onRemoveInputFocusAndAddSearchTermToLocalStorage,
    handleRemoveInputFocus,
    closeSearchResult,
    handleOnClickSearchIcon,
    handleCloseIconTap,
    handleInputOnChange,
    shouldShowCloseIcon,
    collapseAndBlurInput,
    suggestionSelected: suggestionSelected.current,
    setSuggestionSelected,
  };
};
